import React, { useState } from 'react';
import { generateHooks, generateScript, generateCalendar, VOIX_IA, VIDEO_STYLES, HOOK_TYPES, HOOK_PLATFORMS, calculerScoreViral, genererMiniature } from '../services/aiService';
import { mergeAll } from '../services/videoMerge';
import { useAuth } from '../contexts/AuthContext';
import { ErrorBanner } from '../components/UIStates';

const WORKER = process.env.REACT_APP_WORKER_URL || 'https://contenuia-proxy.soihibaliousseni.workers.dev';

/** Styles responsive partagés par les pages de ce fichier. */
const ResponsiveStyles = () => (
  <style>{`
    @media (max-width: 768px) {
      .op-grid2 { grid-template-columns: 1fr !important; }
      .op-grid3 { grid-template-columns: 1fr !important; }
    }
  `}</style>
);
const inp: React.CSSProperties = { width: '100%', background: '#252540', border: '1px solid #3A3A5C', borderRadius: 10, padding: '12px 14px', color: '#F8F7FF', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const C = {
  card: { background: '#1A1A2E', border: '1px solid #3A3A5C', borderRadius: 20, padding: 24, marginBottom: 20 } as React.CSSProperties,
  label: { display: 'block', fontSize: 13, color: '#C8C8E8', marginBottom: 6, fontWeight: 500 } as React.CSSProperties,
  btn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 28px', background: 'linear-gradient(135deg,#6C47FF,#8B6FFF)', border: 'none', borderRadius: 14, color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%', marginTop: 16 } as React.CSSProperties,
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' } as React.CSSProperties,
};

// ===== HOOKS PAGE =====
export const HooksPage: React.FC = () => {
  const [form, setForm] = useState({ produit: '', cible: '', type: 'choc', plateforme: 'tiktok', langue: 'Français' });
  const [hooks, setHooks] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(-1);

  const handle = async () => {
    setError('');
    if (!form.produit.trim()) { setError('Renseignez le nom du produit.'); return; }
    if (!form.cible.trim()) { setError('Renseignez la cible.'); return; }
    setLoading(true);
    try {
      const res = await generateHooks(form);
      if (!res.length) setError("Aucun hook n'a pu être généré. Réessayez.");
      setHooks(res);
    } catch (e: any) {
      setError(e?.message || "La génération a échoué. Réessayez.");
    }
    setLoading(false);
  };

  return (
    <div style={{ padding: 32 }}>
      <ResponsiveStyles />
      <div style={{ fontSize: 28, fontWeight: 800, color: '#F8F7FF', marginBottom: 6 }}>🎣 Hooks viraux</div>
      <div style={{ color: '#C8C8E8', fontSize: 14, marginBottom: 20 }}>Les phrases qui arrêtent le scroll</div>
      <ErrorBanner message={error} onRetry={handle} onDismiss={() => setError('')} />
      <div style={C.card}>
        <div className="op-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div><label style={C.label}>Produit *</label><input style={inp} value={form.produit} onChange={e => setForm({ ...form, produit: e.target.value })} placeholder="ContenuIA" /></div>
          <div><label style={C.label}>Cible *</label><input style={inp} value={form.cible} onChange={e => setForm({ ...form, cible: e.target.value })} placeholder="Entrepreneurs" /></div>
          <div><label style={C.label}>Type</label><select style={inp} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>{HOOK_TYPES.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>)}</select></div>
          <div><label style={C.label}>Plateforme</label><select style={inp} value={form.plateforme} onChange={e => setForm({ ...form, plateforme: e.target.value })}>{HOOK_PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}</select></div>
        </div>
        <button style={{ ...C.btn, ...(loading ? C.btnDisabled : {}) }} onClick={handle} disabled={loading}>{loading ? '⏳...' : '🎣 Générer 10 hooks'}</button>
      </div>
      {hooks.length > 0 && hooks.map((h, i) => (
        <div key={i} style={{ ...C.card, padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 20, fontWeight: 800, color: '#6C47FF', minWidth: 28 }}>{i + 1}</span>
          <span style={{ flex: 1, fontSize: 14, color: '#C8C8E8' }}>{h}</span>
          <button onClick={() => { navigator.clipboard.writeText(h); setCopied(i); setTimeout(() => setCopied(-1), 2000); }} style={{ background: 'none', border: '1px solid #3A3A5C', borderRadius: 6, padding: '4px 10px', color: copied === i ? '#00F5A0' : '#C8C8E8', fontSize: 11, cursor: 'pointer' }}>{copied === i ? '✅' : '📋'}</button>
        </div>
      ))}
    </div>
  );
};

// ===== SCRIPTS PAGE =====
export const ScriptsPage: React.FC = () => {
  const [form, setForm] = useState({ produit: 'ContenuIA', message: '', duree: '30', style: 'youtube', voix: 'fr-femme-1', langue: 'Français' });
  const [script, setScript] = useState('');
  const [audioB64, setAudioB64] = useState('');
  const [pexelsData, setPexelsData] = useState<any>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [miniatureB64, setMiniatureB64] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const show = (m: string) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const handleScript = async () => {
    setError('');
    if (!form.produit.trim()) { setError('Renseignez le nom du produit.'); return; }
    setLoading(true); setLoadingStep('✍️ Script...');
    try {
      const s = await generateScript({ produit: form.produit, duree: form.duree, format: VIDEO_STYLES[form.style]?.label || 'YouTube', langue: form.langue, message: form.message, persona: 'mrbeast' });
      setScript(s);
      setLoadingStep('🔊 Voix IA...');
      const voix = VOIX_IA[form.voix];
      const ttsRes = await fetch(WORKER + '/tts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: s.replace(/\[.*?\]/g, '').substring(0, 800), languageCode: voix.languageCode, voiceName: voix.voiceName }) });
      const ttsData = await ttsRes.json();
      if (ttsData.audioContent) { setAudioB64(ttsData.audioContent); show('✅ Script + voix prêts !'); }
      setLoadingStep('🎬 Vidéo Pexels...');
      const q = form.produit + ' business professional';
      const vRes = await fetch(WORKER + '/pexels-video?q=' + encodeURIComponent(q) + '&page=' + (Math.floor(Math.random() * 10) + 1));
      const vData = await vRes.json();
      if (vData.url) { setPexelsData(vData); show('✅ Voix + vidéo prêtes !'); }
    } catch (e: any) {
      setError(e?.message || "La génération a échoué. Réessayez.");
      console.error('[Scripts]', e);
    }
    setLoading(false); setLoadingStep('');
  };

  const handleCreateVideo = async () => {
    setError('');
    if (!audioB64) { setError("Générez d'abord le script et la voix."); return; }
    if (!pexelsData?.url) { setError("Aucune vidéo disponible. Relancez la génération."); return; }
    setLoading(true);
    try {
      const proxyUrl = WORKER + '/proxy-video?url=' + encodeURIComponent(pexelsData.url);
      const blob = await mergeAll({ pexelsVideoUrl: proxyUrl, audioB64, script, produit: form.produit, onProgress: (s, p) => setLoadingStep(s + ' — ' + p + '%') });
      setVideoBlob(blob); setVideoUrl(URL.createObjectURL(blob));
      show('✅ MP4 prêt !');
      try { const mini = await genererMiniature({ titre: form.message || form.produit, produit: form.produit, score: 85 }); setMiniatureB64(mini); } catch {}
    } catch (e: any) {
      setError(e?.message || "Le montage vidéo a échoué. Utilisez Chrome ou Edge récent.");
      console.error('[Scripts] video:', e);
    }
    setLoading(false); setLoadingStep('');
  };

  const handleDownload = () => {
    if (!videoBlob) return;
    const a = document.createElement('a'); a.href = URL.createObjectURL(videoBlob); a.download = form.produit.replace(/\s+/g, '_') + '_contenuia.mp4';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleUploadYouTube = async () => {
    setError('');
    if (!videoBlob) { setError("Créez d'abord la vidéo."); return; }
    const token = localStorage.getItem('yt_access_token');
    if (!token) { setError("Connectez YouTube depuis la page « YouTube Shorts »."); return; }
    setUploadLoading(true);
    try {
      const arr = await videoBlob.arrayBuffer(); const u8 = new Uint8Array(arr); let bin = ''; for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]); const vb64 = btoa(bin);
      const res = await fetch(WORKER + '/youtube-upload', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ access_token: token, video_b64: vb64, metadata: { snippet: { title: ((form.message || form.produit) + ' #Shorts').substring(0, 100), description: 'Créé avec ContenuIA', tags: ['ContenuIA', 'IA', 'Shorts'], categoryId: '22' }, status: { privacyStatus: 'public', selfDeclaredMadeForKids: false } } }) });
      const data = await res.json();
      if (data.ok) show('🎉 Publié sur YouTube !');
      else setError(data.error || "La publication YouTube a échoué. Vérifiez votre connexion au compte.");
    } catch (e: any) {
      setError("La publication YouTube a échoué. Réessayez.");
      console.error('[Scripts] youtube:', e);
    }
    setUploadLoading(false);
  };

  const { score } = script ? calculerScoreViral(script) : { score: 0 };

  return (
    <div style={{ padding: 32 }}>
      <ResponsiveStyles />
      <div style={{ fontSize: 28, fontWeight: 800, color: '#F8F7FF', marginBottom: 6 }}>🎬 Scripts vidéo</div>
      <div style={{ color: '#C8C8E8', fontSize: 14, marginBottom: 20 }}>Script + voix IA + vidéo en un clic</div>
      <ErrorBanner message={error} onDismiss={() => setError('')} />
      <div style={C.card}>
        <div className="op-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div><label style={C.label}>Produit *</label><input style={inp} value={form.produit} onChange={e => setForm({ ...form, produit: e.target.value })} /></div>
          <div><label style={C.label}>Style</label><select style={inp} value={form.style} onChange={e => setForm({ ...form, style: e.target.value })}>{Object.entries(VIDEO_STYLES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}</select></div>
          <div style={{ gridColumn: '1/-1' }}><label style={{ ...C.label, color: '#FF4444', fontWeight: 700 }}>🎯 Titre / Angle de la vidéo *</label><input style={{ ...inp, border: '2px solid rgba(255,68,68,0.4)', fontSize: 15 }} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder='ex: "Cette IA crée des vidéos pendant que tu travailles"' /></div>
          <div><label style={C.label}>Voix</label><select style={inp} value={form.voix} onChange={e => setForm({ ...form, voix: e.target.value })}>{Object.entries(VOIX_IA).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}</select></div>
          <div><label style={C.label}>Durée</label><select style={inp} value={form.duree} onChange={e => setForm({ ...form, duree: e.target.value })}><option value="15">15s</option><option value="30">30s</option><option value="60">60s</option></select></div>
        </div>
        <button style={{ ...C.btn, ...(loading ? C.btnDisabled : {}) }} onClick={handleScript} disabled={loading}>{loading ? loadingStep || '⏳...' : '✍️ Générer le script'}</button>
      </div>

      {script && (
        <div style={C.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#8B6FFF' }}>📝 Script généré</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: score >= 80 ? '#00F5A0' : '#FFB300' }}>{score}/100</span>
          </div>
          <div style={{ fontSize: 14, color: '#C8C8E8', lineHeight: 1.8, whiteSpace: 'pre-wrap', marginBottom: 16 }}>{script}</div>
          {audioB64 && <div style={{ marginBottom: 12 }}><span style={{ fontSize: 12, color: '#00F5A0', fontWeight: 700 }}>🔊 Voix IA — {VOIX_IA[form.voix]?.label}</span><audio controls src={'data:audio/mp3;base64,' + audioB64} style={{ width: '100%', marginTop: 6 }} /></div>}
          {pexelsData && <div style={{ marginBottom: 12 }}><span style={{ fontSize: 12, color: '#8B6FFF', fontWeight: 700 }}>📹 Vidéo sélectionnée</span><img src={pexelsData.thumbnail} alt="pexels" style={{ width: '100%', borderRadius: 10, maxHeight: 140, objectFit: 'cover', marginTop: 6 }} /><div style={{ fontSize: 11, color: '#C8C8E8', marginTop: 4 }}>Par {pexelsData.author} — Libre de droits ✅</div></div>}
          <button style={{ ...C.btn, background: 'linear-gradient(135deg,#6C47FF,#8B6FFF)', ...(loading ? C.btnDisabled : {}) }} onClick={handleCreateVideo} disabled={loading}>🎬 Obtenir le MP4</button>
        </div>
      )}

      {videoUrl && (
        <div style={C.card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#00F5A0', marginBottom: 12 }}>✅ Vidéo prête</div>
          <video controls src={videoUrl} style={{ width: '100%', borderRadius: 12, maxHeight: 300 }} />
          {miniatureB64 && <div style={{ marginTop: 12 }}><div style={{ fontSize: 12, color: '#8B6FFF', fontWeight: 700, marginBottom: 6 }}>🖼️ Miniature YouTube</div><img src={miniatureB64} alt="miniature" style={{ width: '100%', borderRadius: 10 }} /></div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button onClick={handleDownload} style={{ ...C.btn, marginTop: 0, background: '#252540', border: '1px solid #3A3A5C' }}>⬇️ MP4</button>
            <button onClick={handleUploadYouTube} disabled={uploadLoading} style={{ ...C.btn, marginTop: 0, flex: 1, background: uploadLoading ? '#3A3A5C' : '#FF0000' }}>{uploadLoading ? '⏳...' : '🔴 YouTube'}</button>
          </div>
        </div>
      )}
      <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#1A1A2E', border: '1px solid #00F5A0', borderRadius: 12, padding: '12px 20px', color: '#00F5A0', zIndex: 2000, fontSize: 13, opacity: toast ? 1 : 0, transition: 'all 0.3s', pointerEvents: 'none' }}>{toast}</div>
    </div>
  );
};

// ===== CALENDAR PAGE =====
export const CalendarPage: React.FC = () => {
  const [form, setForm] = useState({ produit: 'ContenuIA', secteur: 'B2B SaaS', langue: 'Français', frequence: '1' });
  const [days, setDays] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const handle = async () => {
    setError('');
    if (!form.produit.trim()) { setError('Renseignez le nom du produit.'); return; }
    setLoading(true);
    try {
      const res = await generateCalendar(form);
      if (!res.length) setError("Le calendrier n'a pas pu être généré. Réessayez.");
      setDays(res);
    } catch (e: any) {
      setError(e?.message || "La génération a échoué. Réessayez.");
    }
    setLoading(false);
  };
  const typeColors: Record<string, string> = { Post: '#8B6FFF', Hook: '#00F5A0', Video: '#FF4444', Email: '#FFB300', Carrousel: '#00BFFF' };
  return (
    <div style={{ padding: 32 }}>
      <ResponsiveStyles />
      <div style={{ fontSize: 28, fontWeight: 800, color: '#F8F7FF', marginBottom: 6 }}>📅 Calendrier éditorial</div>
      <div style={{ color: '#C8C8E8', fontSize: 14, marginBottom: 20 }}>30 jours de contenu en 1 clic</div>
      <ErrorBanner message={error} onRetry={handle} onDismiss={() => setError('')} />
      <div style={C.card}>
        <div className="op-grid2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div><label style={C.label}>Produit</label><input style={inp} value={form.produit} onChange={e => setForm({ ...form, produit: e.target.value })} /></div>
          <div><label style={C.label}>Secteur</label><select style={inp} value={form.secteur} onChange={e => setForm({ ...form, secteur: e.target.value })}><option>B2B SaaS</option><option>E-commerce</option><option>Services locaux</option><option>Formation</option><option>Restauration</option></select></div>
        </div>
        <button style={{ ...C.btn, ...(loading ? C.btnDisabled : {}) }} onClick={handle} disabled={loading}>{loading ? '⏳...' : '📅 Générer 30 jours'}</button>
      </div>
      {days.length > 0 && <div className="op-grid3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
        {days.slice(0, 30).map((d, i) => (
          <div key={i} style={{ ...C.card, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontWeight: 800, color: '#8B6FFF' }}>J{d.day}</span>
              <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: (typeColors[d.type] || '#8B6FFF') + '22', color: typeColors[d.type] || '#8B6FFF' }}>{d.type}</span>
            </div>
            <div style={{ fontSize: 11, color: '#6C6C8A', marginBottom: 4 }}>{d.theme}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#F8F7FF' }}>{d.titre}</div>
          </div>
        ))}
      </div>}
    </div>
  );
};

// ===== PLANS PAGE =====
export const PlansPage: React.FC = () => {
  const { user } = useAuth();
  const plans = [
    { name: 'Free', price: '0€', features: ['10 contenus/mois', '5 langues', 'Posts LinkedIn & Instagram'], current: user?.plan === 'free' },
    { name: 'Pro', price: '79€', features: ['500 contenus/mois', '45 langues', 'Tous les réseaux', 'Scripts vidéo', 'YouTube auto', 'Hooks illimités'], current: user?.plan === 'pro', featured: true },
    { name: 'Agency', price: '299€', features: ['Illimité', '45 langues', 'Multi-workspaces', 'API access', 'Support prioritaire'], current: user?.plan === 'agency' },
  ];
  return (
    <div style={{ padding: 32 }}>
      <ResponsiveStyles />
      <div style={{ fontSize: 28, fontWeight: 800, color: '#F8F7FF', marginBottom: 28 }}>💎 Abonnements</div>
      <div className="op-grid3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
        {plans.map((p, i) => (
          <div key={i} style={{ background: '#1A1A2E', border: p.featured ? '2px solid #6C47FF' : '1px solid #3A3A5C', borderRadius: 20, padding: 28, position: 'relative' }}>
            {p.featured && <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#6C47FF', color: '#FFF', padding: '4px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>⚡ Populaire</div>}
            <div style={{ fontSize: 20, fontWeight: 800, color: '#F8F7FF', marginBottom: 8 }}>{p.name}</div>
            <div style={{ fontSize: 40, fontWeight: 800, color: '#8B6FFF', marginBottom: 4 }}>{p.price}<span style={{ fontSize: 16, color: '#C8C8E8' }}>/mois</span></div>
            <div style={{ margin: '20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {p.features.map((f, j) => <div key={j} style={{ fontSize: 13, color: '#C8C8E8' }}>✓ {f}</div>)}
            </div>
            <button style={{ width: '100%', padding: 12, borderRadius: 12, border: p.current ? 'none' : '1px solid #6C47FF', background: p.current ? '#6C47FF' : 'transparent', color: p.current ? '#FFF' : '#8B6FFF', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {p.current ? 'Plan actuel' : 'Passer à ' + p.name}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
